import { Alert, Box, Button, CircularProgress } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ClientPartTypeField } from './sections/ClientPartTypeField';
import { FiltersSection } from './sections/FiltersSection';
import { JobDetailsTail } from './sections/JobDetailsTail';
import { LocationField } from './sections/LocationField';
import { PartDetailsSection } from './sections/PartDetailsSection';
import { PriorityField } from './sections/PriorityField';
import { SectionGroup } from './sections/SectionGroup';
import { ServiceTypeFields } from './sections/ServiceTypeFields';
import { useCreateService } from './useCreateService';
import { ServiceCreationRuleTypes } from '../../../configs/enums';
import { resolveModelForPartType } from '../../../utils/reported-model';
import { isRuleExist } from '../../../utils/services';

export type CreateServiceModel = ReturnType<typeof useCreateService>;

export interface CreateServiceFormProps {
  model: CreateServiceModel;
}

export const CreateServiceForm: React.FC<CreateServiceFormProps> = ({ model }) => {
  const {
    values,
    updateValues,
    onMappingChange,
    showOverride,
    setShowOverride,
    hierarchy,
    locationSearch,
    serviceTypeOptions,
    attributeOptions,
    rawModelAttributes,
    mappingsLoading,
    descriptionOptions,
    descriptionRequired,
    reference1Required,
    reference2Required,
    reference3Required,
    selectedRules,
    errors,
    validate,
    isFormValid,
    onSubmit,
    isSubmitting,
    submitError,
    hasAllocationPermission,
    isClientCustomerUser,
  } = model;

  const hasLocation = !!locationSearch.state.locationId;
  const mappingSelected = !!values.serviceTypeMapping || showOverride;
  const priorityReady = !!hierarchy.selection.parentId;
  const faultRequired = isRuleExist(selectedRules, ServiceCreationRuleTypes.FaultAndDescription);
  const modelRequired = isRuleExist(selectedRules, ServiceCreationRuleTypes.Model);
  const serialRequired = isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber);
  const notesRequired = isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes);

  const locationLock = !hasLocation ? 'Choose a location first' : undefined;
  // Client-customer users don't see a hierarchy selector — the hierarchy auto-syncs
  // from the chosen location, so the prompt is purely transient.
  const resolveHierarchyCopy = isClientCustomerUser
    ? 'Resolving location…'
    : 'Select a client / contract to continue';
  const hierarchyLock = !priorityReady
    ? hasLocation
      ? resolveHierarchyCopy
      : 'Choose a location first'
    : undefined;
  const mappingLock = !mappingSelected ? 'Pick a service type to continue' : undefined;

  const onLocationSearch = (name: string) =>
    locationSearch.search(
      name,
      hierarchy.selection.brandSelectionId !== '0'
        ? hierarchy.selection.brandSelectionId
        : hierarchy.selection.parentId,
      hierarchy.selection.clientId || undefined
    );

  const serviceTypeFields = (
    <ServiceTypeFields
      values={values}
      options={serviceTypeOptions}
      loading={mappingsLoading}
      showOverride={showOverride}
      onToggleOverride={setShowOverride}
      onMappingChange={onMappingChange}
      onChange={updateValues}
      validate={validate && !hierarchyLock}
      error={!hierarchyLock ? errors.mapping : undefined}
      lockReason={!locationLock ? hierarchyLock : undefined}
      locked={!!locationLock}
    />
  );

  const priorityField = (
    <PriorityField
      value={values.priorityCode}
      items={attributeOptions.priorities}
      onChange={(v) => updateValues({ priorityCode: v })}
      validate={validate && !hierarchyLock && !mappingLock}
      disabled={!priorityReady || !mappingSelected}
      lockReason={!locationLock ? (hierarchyLock ?? mappingLock) : undefined}
      locked={!!locationLock}
      error={!hierarchyLock && !mappingLock ? errors.priority : undefined}
    />
  );

  const fullPartDetails = (
    <PartDetailsSection
      reportedSerialNumber={values.reportedSerialNumber}
      reportedPartTypeName={values.reportedPartTypeName}
      serialRequired={serialRequired}
      clientId={hierarchy.selection.clientId}
      contractId={hierarchy.selection.contractId}
      onSerialChange={(value, partId, partTypeName, partData) => {
        updateValues({
          reportedSerialNumber: value,
          reportedSerialNumberPartId: partId,
          reportedPartTypeName: partTypeName ?? '',
        });
        if (partData?.locationId && !hierarchy.selection.clientId) {
          hierarchy.syncFromLocationId(partData.locationId);
        }
      }}
      serialError={validate ? errors.serialNumber : undefined}
      reportedModel={values.reportedModel}
      modelOptions={attributeOptions.modelOptions}
      modelRequired={modelRequired}
      onModelChange={(v) => updateValues({ reportedModel: v })}
      modelError={validate ? errors.model : undefined}
      mainFaultId={values.mainFaultId}
      descriptionId={values.descriptionId}
      mainFaultOptions={attributeOptions.mainFaultOptions}
      descriptionOptions={descriptionOptions}
      descriptionRequired={descriptionRequired}
      faultsLoading={attributeOptions.faultsLoading}
      hasNoFaults={attributeOptions.hasNoFaults}
      faultRequired={faultRequired}
      onMainFaultChange={(v) => updateValues({ mainFaultId: v, descriptionId: '' })}
      onDescriptionChange={(v) => updateValues({ descriptionId: v })}
      validate={validate}
      contextDisabled={!values.reportedSerialNumber}
    />
  );

  // Client-customer users get a Part Type-only picker, populated from the
  // pre-configured list at the client/contract level. The wire payload sends
  // the part type via the resolved Reported Model (rm) attribute — models are
  // configured with a `partType[]` list and the BFF derives the service's
  // part type from that. The rsn attribute is reserved for serialised parts
  // (whose value is a Part record id, not a partType id), so we leave the
  // reportedSerialNumber* fields untouched here.
  const clientPartType = (
    <ClientPartTypeField
      clientId={hierarchy.selection.clientId}
      contractId={hierarchy.selection.contractId}
      partTypeId={values.reportedPartTypeId}
      partTypeName={values.reportedPartTypeName}
      onChange={(partType) => {
        const matchedModel = resolveModelForPartType(rawModelAttributes, partType?.id);
        updateValues({
          reportedPartTypeId: partType?.id,
          reportedPartTypeName: partType?.name ?? '',
          reportedModel: matchedModel ? String(matchedModel.id) : '',
        });
      }}
      locked={!!locationLock || !priorityReady}
      lockReason={!locationLock ? hierarchyLock : undefined}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SectionGroup title="Location & Client">
        <LocationField
          mainLocationNode={locationSearch.state.mainLocationNode}
          subLocationNode={locationSearch.state.subLocationNode}
          subLocationOptions={locationSearch.state.subLocationOptions}
          locationText={locationSearch.state.locationText}
          searchError={locationSearch.state.searchError}
          onSearch={onLocationSearch}
          onSelect={locationSearch.onSelect}
          onSelectSubLocation={locationSearch.onSelectSubLocation}
          onClear={() => {
            locationSearch.clear();
            hierarchy.reset();
          }}
          onDismissOptions={locationSearch.dismissOptions}
          error={validate ? errors.location : undefined}
        />
        {!isClientCustomerUser && (
          <FiltersSection
            clients={hierarchy.clients}
            contracts={hierarchy.contracts}
            brands={hierarchy.brands}
            clientId={hierarchy.selection.clientId}
            contractId={hierarchy.selection.contractId}
            brandId={hierarchy.selection.brandId}
            error={validate ? errors.hierarchy : undefined}
            onChangeNodeType={hierarchy.onChangeNodeType}
            onParentSelect={hierarchy.onParentSelect}
            canClear={hasLocation || hierarchy.selection.clientId > 0}
            onClear={() => {
              locationSearch.clear();
              hierarchy.reset();
            }}
          />
        )}
      </SectionGroup>
      {!isClientCustomerUser && <SectionGroup title="Part Details">{fullPartDetails}</SectionGroup>}
      <SectionGroup title="Job Details" lockReason={locationLock}>
        {serviceTypeFields}
        {priorityField}
        {isClientCustomerUser && clientPartType}
        <JobDetailsTail
          values={values}
          errors={errors}
          validate={validate}
          reference1Required={reference1Required}
          reference2Required={reference2Required}
          reference3Required={reference3Required}
          notesRequired={notesRequired}
          hasAllocationPermission={hasAllocationPermission}
          locked={!!locationLock}
          updateValues={updateValues}
        />
      </SectionGroup>
      {submitError && (
        <Alert severity="error" sx={{ fontSize: '13px' }}>
          {submitError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting || !isFormValid}
          aria-busy={isSubmitting}
          aria-label={isSubmitting ? 'Creating service' : 'Create service'}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 160 }}
        >
          {isSubmitting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Create Service
        </Button>
        <Button component={RouterLink} to="/services" variant="text" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
